import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import Underline from "@tiptap/extension-underline";
import { TextStyleKit } from "@tiptap/extension-text-style";
import TiptapHighlight from "@tiptap/extension-highlight";
import "../css/tiptap.css";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import HighlightIcon from "@mui/icons-material/Highlight";

import {
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  Button,
  Box,
} from "@mui/material";
const TiptapEditor = ({ value, setValue, mentionUsers, mention= false }) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const [highlightColor, setHighlightColor] = useState("#ffff00");

  const editor = useEditor({
    extensions: [
      StarterKit,

      Underline,

      TextStyleKit,

      TiptapHighlight.configure({
        multicolor: true,
      }),

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),

      Placeholder.configure({
        placeholder: "Write Offer Letter...",
      }),

      TableKit.configure({
        resizable: true,
      }),
    ],
    editorProps: {
      attributes: {
        style: "color: #000;",
      },
    },
    content: value || "",

    onUpdate({ editor }) {
      const html = editor.getHTML().replace(/<p><\/p>/g, "<p>&nbsp;</p>");
      setValue(html);
    },
    onSelectionUpdate() {
      forceUpdate();
    },
    onTransaction() {
      forceUpdate();
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) return null;

  const getHeadingValue = () => {
    if (editor.isActive("heading", { level: 3 })) return "h3";
    if (editor.isActive("heading", { level: 4 })) return "h4";
    if (editor.isActive("heading", { level: 5 })) return "h5";
    if (editor.isActive("heading", { level: 6 })) return "h6";

    return "p";
  };
  return (
    <div className="editor-wrapper" style={{maxWidth: "100%"}}>
      <div className="toolbar">
        <Tooltip title="Undo">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().undo().run()}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Redo">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().redo().run()}
          >
            <RedoIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />
        {/* Heading Dropdown */}

        <Select
          size="small"
          value={getHeadingValue()}
          onChange={(e) => {
            const headingValue = e.target.value;

            if (headingValue === "p") {
              editor.chain().focus().setParagraph().run();
              return;
            }

            editor
              .chain()
              .focus()
              .toggleHeading({
                level: Number(headingValue.replace("h", "")),
              })
              .run();
          }}
        >
          <MenuItem value="p">Paragraph</MenuItem>
          <MenuItem value="h3">Heading 3</MenuItem>
          <MenuItem value="h4">Heading 4</MenuItem>
          <MenuItem value="h5">Heading 5</MenuItem>
          <MenuItem value="h6">Heading 6</MenuItem>
        </Select>
        <Tooltip title="Bold">
          <IconButton
            size="small"
            color={editor.isActive("bold") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic">
          <IconButton
            size="small"
            color={editor.isActive("italic") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Underline">
          <IconButton
            size="small"
            color={editor.isActive("underline") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Align Left">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <FormatAlignLeftIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Center">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <FormatAlignCenterIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Right">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <FormatAlignRightIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Bullet List">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Number List">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumberedIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Highlight Text">
            <IconButton
              size="small"
              color={editor.isActive("highlight") ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleHighlight().run()}
            >
              <HighlightIcon />
            </IconButton>
          </Tooltip>

          <input
            type="color"
            value={highlightColor}
            onChange={(e) => {
              setHighlightColor(e.target.value);

              editor
                .chain()
                .focus()
                .setHighlight({
                  color: e.target.value,
                })
                .run();
            }}
          />
        </Box>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Insert Table">
          <IconButton
            size="small"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({
                  rows: 3,
                  cols: 3,
                  withHeaderRow: true,
                })
                .run()
            }
          >
            <TableChartIcon />
          </IconButton>
        </Tooltip>

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          Row
        </Button>
        <Tooltip title="Delete Row">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
        >
          Column
        </Button>

        <Tooltip title="Delete Column">
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="editor-body">
        <EditorContent editor={editor} />
      </div>

      {/* Variable Buttons */}
      {mention && <div className="variable-container">
        <Select
            size="small"
            displayEmpty
            sx={{ minWidth: 220 }}
            value=""
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                },
              },
            }}
            onChange={(e) => {
            if (!e.target.value) return;

            editor.chain().focus().insertContent(`@{${e.target.value}}`).run();
            }}
        >
            <MenuItem value="">Insert Variable</MenuItem>

            {mentionUsers?.map((item) => (
            <MenuItem key={item.name} value={item.name}>
                {item.label}
            </MenuItem>
            ))}
        </Select>
       
      </div>}
    </div>
  );
};

export default TiptapEditor;
